import {forwardRef} from "react";
import {Button, ButtonProps} from "@mui/material";
import {keyframes, styled} from "@mui/material/styles";
import {Link} from "react-router-dom";
import {t} from "../tokens";

// ─── declare module для color="neutral" ────────────────────────────────────
// Перенесён в themePrimitives.tsx — он живёт рядом с самой palette.neutral
// определением, что делает связь "тип ↔ palette" очевидной.

/**
 * ============================================================================
 * MyButton — кнопка дизайн-системы Parallax.
 * ============================================================================
 *
 * Что добавляет поверх <Button> MUI:
 *
 * 1. Размерная шкала small/medium/large (32/40/44px) согласно дизайн-стандарту.
 *    Default — medium.
 *
 * 2. loading-состояние с shimmer-анимацией. Кнопка остаётся кликабельной?
 *    Нет: pointerEvents:none, cursor:default. Текст остаётся видимым,
 *    поверх него бежит блик. aria-busy выставлен для screen readers.
 *
 * 3. Поддержка color="neutral" — для вторичных действий ("Отмена", "Назад",
 *    "Фильтры"). palette.neutral определена в themePrimitives.tsx.
 *
 * 4. Навигация через `to` / `href` props без болезни с component-prop:
 *
 *      <MyButton to={routes.x}>Добавить</MyButton>     // React Router Link
 *      <MyButton href="https://...">Внешняя</MyButton>  // обычная <a>
 *      <MyButton onClick={...}>Действие</MyButton>      // обычная button
 *
 *    Почему именно так, а не <MyButton component={Link} to=...>:
 *      ButtonProps — это polymorphic тип через generic. Когда мы оборачиваем
 *      его в interface MyButtonProps extends Omit<ButtonProps, ...>, generic
 *      теряется и TypeScript перестаёт знать про polymorphic-props (`to`,
 *      `href` через component=Link). API через `to`/`href` решает проблему
 *      без сложных generic'ов и заодно делает интерфейс декларативным:
 *      потребитель говорит "куда вести", а не "какой тег рендерить".
 *
 *    Для экзотических случаев (component={SomeCustomComponent}) — используй
 *    обычный <Button> MUI: глобальные стили из theme.components.MuiButton
 *    применятся и к нему.
 *
 * 5. Focus-ring через t.shadows.focusRing (для клавиатурной навигации).
 *
 * 6. Press-эффект: scale(0.98) при :active — даёт тактильный отклик.
 * ============================================================================
 */

interface MyButtonProps extends Omit<ButtonProps, "color" | "href"> {
    loading?: boolean;
    /** color="neutral" — для outlined вторичных действий. */
    color?: ButtonProps["color"] | "neutral";
    /**
     * React Router путь. Рендерит кнопку как <Link to={...}>.
     * Поддерживает Cmd+Click для открытия в новой вкладке, корректный <a href>
     * в DOM для screen readers.
     */
    to?: string;
    /**
     * Внешняя ссылка. Рендерит кнопку как <a href={...}>.
     * Используй для ссылок вне приложения. Для внутренней навигации — `to`.
     */
    href?: string;
    /** target для href (например, "_blank" для нового окна). */
    target?: string;
    /** rel для href (например, "noopener noreferrer" для target="_blank"). */
    rel?: string;
}

/**
 * Бесконечный бегущий блик. linear-easing критично:
 * с ease-in-out видны микро-остановки между итерациями.
 */
const shimmerSlide = keyframes`
    0% {
        background-position: 200% 0;
    }
    100% {
        background-position: -200% 0;
    }
`;

/** Размерная шкала кнопки */
const SIZING = {
    small: {height: 32, paddingInline: 12, fontSize: "0.8125rem", iconSize: 16},
    medium: {height: 40, paddingInline: 16, fontSize: "0.875rem", iconSize: 18},
    large: {height: 44, paddingInline: 20, fontSize: "0.9375rem", iconSize: 20},
} as const;

const StyledButton = styled(Button, {
    shouldForwardProp: (prop) => prop !== "loading",
})<MyButtonProps>(({theme, size = "medium", loading}) => {
    const sizing = SIZING[size as keyof typeof SIZING];

    return {
        textTransform: "none",
        borderRadius: t.radius.md,
        height: sizing.height,
        paddingInline: sizing.paddingInline,
        fontSize: sizing.fontSize,
        fontWeight: 500,
        letterSpacing: "0.01em",
        lineHeight: 1,
        gap: 6,
        position: "relative",
        overflow: "hidden",
        textDecoration: "none",            // когда component=Link/a, убираем подчёркивание
        transition: [
            `background-color ${t.duration.base}ms`,
            `border-color ${t.duration.base}ms`,
            `color ${t.duration.base}ms`,
            `opacity ${t.duration.base}ms`,
            `transform ${t.duration.base}ms`,
            `box-shadow ${t.duration.base}ms`,
        ].join(", "),

        "& .MuiButton-startIcon": {
            marginLeft: 0,
            marginRight: 4,
            "& > *:first-of-type": {fontSize: sizing.iconSize},
        },
        "& .MuiButton-endIcon": {
            marginLeft: 4,
            marginRight: 0,
            "& > *:first-of-type": {fontSize: sizing.iconSize},
        },

        "&:active:not(.Mui-disabled)": {
            transform: "scale(0.98)",
        },

        "&.Mui-disabled": {
            opacity: 0.5,
        },

        // theme.breakpoints — единственное место, где нужен сам theme,
        // потому что media-queries нельзя выразить CSS-переменной.
        [theme.breakpoints.down("sm")]: {
            ...(size === "small" && {height: 36}),
        },

        "&.Mui-focusVisible": {
            boxShadow: t.shadows.focusRing,
        },

        ...(loading && {
            pointerEvents: "none",
            cursor: "default",
            opacity: 0.7,

            "&::before": {
                content: '""',
                position: "absolute",
                inset: 0,
                // Блик задаём литеральным rgba белого. Почему не через токен:
                // под cssVariables t.common.white === "var(--mui-palette-common-white)",
                // а alpha() не парсит var(...) и падает в рантайме ("Unsupported color").
                // Channel-переменная (--mui-...-whiteChannel) в этой теме не генерируется.
                // Блик — световой отблеск, а не семантический цвет: он всегда светлый и
                // не должен меняться между темами, поэтому буквальный белый здесь
                // корректен (исключение по §2.4 ДС — "white осознанно").
                //   - на contained primary (синий фон) → блик отлично виден
                //   - loading на outlined/text редок; там блик слабее, но это не критично.
                background: `linear-gradient(
                    100deg,
                    transparent 35%,
                    rgba(255, 255, 255, 0.45) 50%,
                    transparent 65%
                )`,
                backgroundSize: "200% 100%",
                animation: `${shimmerSlide} 1.5s linear infinite`,
                pointerEvents: "none",
                zIndex: 1,
            },

            "& .MuiButton-startIcon, & .MuiButton-endIcon, & > span": {
                position: "relative",
                zIndex: 2,
            },
        }),
    };
});

/**
 * MyButton рендерится как:
 *   - <Link>  если задан `to`
 *   - <a>     если задан `href`
 *   - <button> по умолчанию
 *
 * Ref-тип — HTMLElement (общий для button/a). На потребителе это редко
 * используется явно, обычно через RouterLink или imperatively (focus()).
 */
const MyButton = forwardRef<HTMLElement, MyButtonProps>(function MyButton(
    {loading, to, href, target, rel, ...rest},
    ref
) {
    // Внутренняя навигация через React Router.
    if (to) {
        return (
            <StyledButton

                loading={loading}
                aria-busy={loading || undefined}
                {...rest}
                component={Link}
                to={to}
            />
        );
    }

    // Внешняя ссылка.
    if (href) {
        // При target="_blank" автоматически добавляем rel="noopener noreferrer"
        // если потребитель не задал свой rel — это защита от tabnabbing атак.
        const safeRel = rel ?? (target === "_blank" ? "noopener noreferrer" : undefined);

        return (
            <StyledButton
                loading={loading}
                aria-busy={loading || undefined}
                {...rest}
                component="a"
                href={href}
                target={target}
                rel={safeRel}
            />
        );
    }

    // Обычная кнопка.
    return (
        <StyledButton
            loading={loading}
            aria-busy={loading || undefined}
            {...rest}
        />
    );
});

export default MyButton;